// src/components/ExtensionRejectionModal.js
import React, { useState, useEffect } from 'react';
import styles from '../styles/ExtensionRejectionModal.module.css'; // Create this CSS file
import { formatDate } from '../utils/styleUtils'; // Adjust path if needed

const ExtensionRejectionModal = ({
    isOpen,
    onClose,
    onSubmit, // Expects onSubmit(reason, newDateString | null)
    taskName,
    currentEndDate,
    requestedEndDate,
    isSubmitting
}) => {
    const [reason, setReason] = useState('');
    const [managerSetEndDate, setManagerSetEndDate] = useState(''); // Manager's proposed new date
    const [error, setError] = useState('');

    // Reset state when modal opens/closes or props change
    useEffect(() => {
        if (isOpen) {
            setReason('');
            setManagerSetEndDate(''); // Clear manager's date input on open
            setError('');
        }
    }, [isOpen]);

    // Format dates for display and min attribute
    const formattedCurrentEndDate = formatDate(currentEndDate, 'yyyy-MM-dd');
    const formattedRequestedEndDate = formatDate(requestedEndDate, 'yyyy-MM-dd');
    // Minimum date for manager's input should be after the original end date
    const minManagerDate = formattedCurrentEndDate
        ? formatDate(new Date(new Date(formattedCurrentEndDate).getTime() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd')
        : '';

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        if (!reason.trim()) {
            setError('Rejection reason is required.');
            return;
        }

        let finalNewDate = null;
        if (managerSetEndDate) {
            const managerDate = new Date(managerSetEndDate);
            const originalDate = currentEndDate || new Date(0);
            if (isNaN(managerDate)) {
                setError('Invalid date format provided by manager.');
                return;
            }
            if (managerDate <= originalDate) {
                setError('Manager-set date must be after the original end date.');
                return;
            }
            finalNewDate = managerSetEndDate; // Pass the string YYYY-MM-DD
        }

        // Call the onSubmit prop passed from TaskDetailPage
        onSubmit(reason.trim(), finalNewDate);
    };

    if (!isOpen) {
        return null; // Don't render anything if modal is closed
    }

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <h3 className={styles.modalTitle}>Reject Extension Request</h3>
                <p className={styles.taskInfo}>
                    Task: <strong>{taskName || 'N/A'}</strong><br />
                    Current End Date: {formatDate(currentEndDate) || 'N/A'}<br />
                    Requested End Date: {formatDate(requestedEndDate) || 'N/A'}
                </p>

                <form onSubmit={handleSubmit}>
                    {error && <p className={styles.errorMessage}>{error}</p>}

                    <div className={styles.inputGroup}>
                        <label htmlFor="rejectionReason">Reason for Rejection (Required):</label>
                        <textarea
                            id="rejectionReason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={4}
                            required
                            disabled={isSubmitting}
                            placeholder="Explain why the extension is being rejected..."
                            className={styles.textareaInput}
                        />
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="managerSetEndDate">
                            Set New End Date (Optional):
                            <span className={styles.labelHint}> Leave blank to keep current date ({formatDate(currentEndDate) || 'N/A'})</span>
                        </label>
                        <input
                            type="date"
                            id="managerSetEndDate"
                            value={managerSetEndDate}
                            onChange={(e) => setManagerSetEndDate(e.target.value)}
                            disabled={isSubmitting}
                            min={minManagerDate} // Set minimum allowed date
                            className={styles.dateInput}
                        />
                         {managerSetEndDate && new Date(managerSetEndDate) <= (currentEndDate || new Date(0)) && (
                            <p className={styles.warningHint}>Warning: Date must be after the current end date.</p>
                        )}
                    </div>

                    <div className={styles.buttonGroup}>
                        <button
                            type="button"
                            onClick={onClose}
                            className={`${styles.formButton} ${styles.cancelButton}`}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`${styles.formButton} ${styles.submitButton}`}
                            disabled={isSubmitting || !reason.trim()}
                        >
                            {isSubmitting ? <span className={styles.loadingSpinner}></span> : 'Submit Rejection'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ExtensionRejectionModal;
